import React, { useEffect, useState } from 'react'
import toast from 'react-hot-toast'
import {Plus, Pencil, Trash2, X, Loader2, FileText, Upload} from 'lucide-react'
import { usePublications, type Publication } from '@/hooks/usePublications'
import { useUpload } from '@/hooks/useUpload'
import { useAuth } from '@/hooks/useAuth'

const CATEGORIES = [
  { value: 'rapports', label: 'Rapports & Études' },
  { value: 'manifestes', label: 'Manifestes & Positions' },
  { value: 'guides', label: 'Guides & Méthodes' },
  { value: 'lettres', label: 'Lettres & Newsletters' },
  { value: 'medias', label: 'Contenus & Médias' },
  { value: 'recherche', label: 'Recherche-Action' },
]

const EMPTY_FORM: Partial<Publication> = {
  title: '',
  category: 'rapports',
  description: '',
  tag: '',
  status: 'draft',
  order: 0,
  fileUrl: '',
  coverUrl: '',
  publishedDate: '',
}

const inputCls =
  'w-full px-4 py-2.5 bg-[#0E1B2C] border border-[#E8DCC4]/15 rounded-lg text-[#E8DCC4] placeholder:text-[#E8DCC4]/30 focus:outline-none focus:border-[#A3C644] transition-colors text-sm'
const labelCls = 'block text-[#C9A86A] text-xs uppercase tracking-widest mb-1.5'

interface FormModalProps {
  initial: Partial<Publication> | null
  onClose: () => void
  onSave: (data: Partial<Publication>) => Promise<void>
}

const FormModal: React.FC<FormModalProps> = ({ initial, onClose, onSave }) => {
  const [form, setForm] = useState<Partial<Publication>>(initial ?? EMPTY_FORM)
  const [saving, setSaving] = useState(false)
  const { upload, uploading } = useUpload()

  const set = (k: keyof Publication, v: string | number) =>
    setForm((p) => ({ ...p, [k]: v }))

  const handleCoverUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    const url = await upload(file)
    if (url) set('coverUrl', url)
    else toast.error('Échec du téléversement de la couverture.')
  }

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    const url = await upload(file)
    if (url) set('fileUrl', url)
    else toast.error('Échec du téléversement du fichier.')
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!form.title?.trim() || !form.description?.trim()) {
      toast.error('Le titre et la description sont obligatoires.')
      return
    }
    setSaving(true)
    try {
      await onSave(form)
      onClose()
    } catch {
      toast.error('Une erreur est survenue. Veuillez réessayer.')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 px-4">
      <div className="w-full max-w-2xl bg-[#14253B] rounded-2xl border border-[#E8DCC4]/10 shadow-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-5 border-b border-[#E8DCC4]/10">
          <h2 className="font-serif text-xl text-[#E8DCC4]">
            {initial?._id ? 'Modifier la publication' : 'Nouvelle publication'}
          </h2>
          <button onClick={onClose} className="text-[#E8DCC4]/40 hover:text-[#E8DCC4] transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-5">
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2">
              <label className={labelCls}>Titre *</label>
              <input className={inputCls} value={form.title ?? ''} onChange={(e) => set('title', e.target.value)} placeholder="Titre de la publication" />
            </div>
            <div>
              <label className={labelCls}>Catégorie</label>
              <select className={inputCls} value={form.category ?? 'rapports'} onChange={(e) => set('category', e.target.value)}>
                {CATEGORIES.map((c) => (
                  <option key={c.value} value={c.value}>{c.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className={labelCls}>Statut</label>
              <select className={inputCls} value={form.status ?? 'draft'} onChange={(e) => set('status', e.target.value)}>
                <option value="draft">Brouillon</option>
                <option value="published">Publié</option>
              </select>
            </div>
            <div className="col-span-2">
              <label className={labelCls}>Description *</label>
              <textarea
                className={inputCls + ' resize-none'}
                rows={3}
                value={form.description ?? ''}
                onChange={(e) => set('description', e.target.value)}
                placeholder="Résumé de la publication..."
              />
            </div>
            <div>
              <label className={labelCls}>Étiquette (tag)</label>
              <input className={inputCls} value={form.tag ?? ''} onChange={(e) => set('tag', e.target.value)} placeholder="ex: Recherche, Vision" />
            </div>
            <div>
              <label className={labelCls}>Ordre d'affichage</label>
              <input type="number" className={inputCls} value={form.order ?? 0} onChange={(e) => set('order', Number(e.target.value))} />
            </div>
            <div>
              <label className={labelCls}>Date de publication</label>
              <input type="date" className={inputCls} value={form.publishedDate?.slice(0, 10) ?? ''} onChange={(e) => set('publishedDate', e.target.value ? new Date(e.target.value).toISOString() : '')} />
            </div>
            <div>
              <label className={labelCls}>Image de couverture</label>
              <div className="flex gap-2 items-center">
                <label className="cursor-pointer flex items-center gap-2 px-4 py-2.5 bg-[#0E1B2C] border border-[#E8DCC4]/15 rounded-lg text-[#E8DCC4]/70 text-sm hover:border-[#A3C644] transition-colors">
                  <Upload className="w-4 h-4" />
                  {uploading ? 'Envoi...' : 'Téléverser'}
                  <input type="file" accept="image/*" className="hidden" onChange={handleCoverUpload} />
                </label>
                {form.coverUrl && <span className="text-[#A3C644] text-xs truncate max-w-[120px]">✓ Image ajoutée</span>}
              </div>
            </div>
            <div>
              <label className={labelCls}>Fichier PDF / Document</label>
              <div className="flex gap-2 items-center">
                <label className="cursor-pointer flex items-center gap-2 px-4 py-2.5 bg-[#0E1B2C] border border-[#E8DCC4]/15 rounded-lg text-[#E8DCC4]/70 text-sm hover:border-[#A3C644] transition-colors">
                  <FileText className="w-4 h-4" />
                  {uploading ? 'Envoi...' : 'Téléverser'}
                  <input type="file" accept=".pdf,.doc,.docx" className="hidden" onChange={handleFileUpload} />
                </label>
                {form.fileUrl && <span className="text-[#A3C644] text-xs truncate max-w-[120px]">✓ Fichier ajouté</span>}
              </div>
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} className="px-5 py-2.5 border border-[#E8DCC4]/15 text-[#E8DCC4]/70 rounded-lg hover:border-[#E8DCC4]/40 transition-colors text-sm">
              Annuler
            </button>
            <button
              type="submit"
              disabled={saving || uploading}
              className="px-5 py-2.5 bg-[#A3C644] text-[#0E1B2C] font-semibold rounded-lg hover:bg-[#b4d655] transition-colors disabled:opacity-60 flex items-center gap-2 text-sm"
            >
              {saving && <Loader2 className="w-4 h-4 animate-spin" />}
              {initial?._id ? 'Enregistrer' : 'Créer'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

const PublicationsManager: React.FC = () => {
  const { publications, loading, fetchPublications, createPublication, updatePublication, deletePublication } = usePublications()
  const { user } = useAuth()
  const [modal, setModal] = useState<Partial<Publication> | null | false>(false)

  useEffect(() => {
    fetchPublications()
  }, [fetchPublications])

  const handleSave = async (data: Partial<Publication>) => {
    if ((data as Publication)._id) {
      await updatePublication((data as Publication)._id, data)
      toast.success('Publication mise à jour.')
    } else {
      await createPublication({ ...data, creator: user?.userId ?? 'admin' })
      toast.success('Publication créée.')
    }
    await fetchPublications()
  }

  const handleDelete = async (pub: Publication) => {
    if (!confirm(`Supprimer "${pub.title}" ?`)) return
    try {
      await deletePublication(pub._id)
      toast.success('Publication supprimée.')
      await fetchPublications()
    } catch {
      toast.error('Erreur lors de la suppression.')
    }
  }

  const statusBadge = (status: string) =>
    status === 'published'
      ? 'bg-[#A3C644]/20 text-[#A3C644] border-[#A3C644]/30'
      : 'bg-[#C9A86A]/20 text-[#C9A86A] border-[#C9A86A]/30'

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="font-serif text-2xl text-[#E8DCC4]">Publications</h2>
        <button
          onClick={() => setModal(EMPTY_FORM)}
          className="flex items-center gap-2 px-4 py-2.5 bg-[#A3C644] text-[#0E1B2C] font-semibold rounded-lg hover:bg-[#b4d655] transition-colors text-sm"
        >
          <Plus className="w-4 h-4" />
          Nouvelle publication
        </button>
      </div>

      {loading ? (
        <div className="flex justify-center py-16">
          <Loader2 className="w-6 h-6 animate-spin text-[#A3C644]" />
        </div>
      ) : publications.length === 0 ? (
        <div className="text-center py-16 text-[#E8DCC4]/40">
          <FileText className="w-10 h-10 mx-auto mb-3 opacity-30" />
          <p className="text-sm">Aucune publication. Créez-en une.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {publications.map((pub) => (
            <div key={pub._id} className="flex items-start gap-4 p-4 bg-[#0E1B2C] rounded-xl border border-[#E8DCC4]/10 hover:border-[#E8DCC4]/20 transition-colors">
              {pub.coverUrl && (
                <img src={pub.coverUrl} alt={pub.title} className="w-14 h-14 object-cover rounded-lg flex-shrink-0" />
              )}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap mb-1">
                  <span className="font-medium text-[#E8DCC4] text-sm truncate">{pub.title}</span>
                  <span className={`text-xs px-2 py-0.5 rounded-full border ${statusBadge(pub.status)}`}>
                    {pub.status === 'published' ? 'Publié' : 'Brouillon'}
                  </span>
                  {pub.tag && (
                    <span className="text-xs px-2 py-0.5 rounded-full bg-[#14253B] text-[#C9A86A] border border-[#C9A86A]/20">
                      {pub.tag}
                    </span>
                  )}
                </div>
                <p className="text-[#E8DCC4]/50 text-xs line-clamp-2">{pub.description}</p>
                <p className="text-[#E8DCC4]/30 text-xs mt-1">
                  {CATEGORIES.find((c) => c.value === pub.category)?.label ?? pub.category}
                </p>
              </div>
              <div className="flex gap-2 flex-shrink-0">
                <button
                  onClick={() => setModal(pub)}
                  className="p-2 text-[#E8DCC4]/40 hover:text-[#C9A86A] transition-colors"
                  title="Modifier"
                >
                  <Pencil className="w-4 h-4" />
                </button>
                <button
                  onClick={() => handleDelete(pub)}
                  className="p-2 text-[#E8DCC4]/40 hover:text-red-400 transition-colors"
                  title="Supprimer"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {modal !== false && (
        <FormModal
          initial={modal}
          onClose={() => setModal(false)}
          onSave={handleSave}
        />
      )}
    </div>
  )
}

export default PublicationsManager
